// H-3, H-4, H-4b, H-4c: creating a Hiring Round.
//
// Three of the four questions here exist for legal reasons rather than product
// ones, and the comments say which:
//   the ROLE must have its capabilities and levels confirmed, because the
//   candidate is graded against those levels (H-4);
//   the HIRING LOCATION decides which notices and consents the candidate sees
//   and how long their data is kept (H-4b, L-1, L-9);
//   the RESTRICTED LOCATION question is asked explicitly and answered
//   explicitly, and a yes refuses the round (H-4c, L-12). The country list
//   already leaves China and Hong Kong out, but New York City is neither a
//   country nor a state, so nothing but the question can catch it.
//   the CONTACT is who the candidate asks for an explanation, a human review or
//   deletion (C-2, L-7), so a round cannot open without one.
//
// The JOB DESCRIPTION is the one product question (DA-5090): it is what Elliott
// drafts the Roleplays from, so they describe this job instead of an invented
// one. Optional, typed or uploaded, and it starts from the one saved on the role.

import React, { useEffect, useMemo, useRef, useState } from 'react';
import { Box, Dialog, Typography } from '@mui/material';
import countryCodeArray from '../../../../constants/countrycode';
import {
  ActionButton,
  BODY,
  DANGER,
  Field,
  FONT,
  INPUT_SX,
  LINE,
  Note,
  SelectField,
  SURFACE,
  TEXT,
  TEXT_2,
  TEXTAREA_SX
} from '../capability-setup/designSystem';
import { US_STATES } from './hiringParts';
import { hiringApi, hiringMessage, type HiringRoleView } from '../../../../api/hiring';
import { useHiringOrg } from '../../../../queries/useHiring';

const JD_TYPES = '.pdf,.docx,.txt,application/pdf,application/vnd.openxmlformats-officedocument.wordprocessingml.document,text/plain';
const MAX_JD = 12000;

const toBase64 = (file: File): Promise<string> =>
  new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.onload = () => resolve(String(reader.result || '').split(',')[1] || '');
    reader.onerror = () => reject(reader.error);
    reader.readAsDataURL(file);
  });

const CreateRoundDialog: React.FC<{
  open: boolean;
  roles: HiringRoleView[];
  blockedCountries: string[];
  blockedMessage: string;
  busy?: boolean;
  error?: string | null;
  onCreate: (input: {
    name: string;
    roleId: string;
    hiringLocation: { country: string; state?: string };
    restrictedLocation: boolean;
    contactEmail: string;
    jobDescription?: string;
    saveJobDescriptionToRole?: boolean;
  }) => void;
  onClose: () => void;
}> = ({ open, roles, blockedCountries, blockedMessage, busy = false, error, onCreate, onClose }) => {
  const [name, setName] = useState('');
  const [roleId, setRoleId] = useState<string | null>(null);
  const [country, setCountry] = useState<string | null>(null);
  const [state, setState] = useState<string | null>(null);
  const [contactEmail, setContactEmail] = useState('');
  // H-4c: unanswered, not assumed. The round cannot be created until the owner
  // has said one way or the other.
  const [restricted, setRestricted] = useState<boolean | null>(null);
  const org = useHiringOrg();
  const [jobDescription, setJobDescription] = useState('');
  const [roleJobDescription, setRoleJobDescription] = useState('');
  const [saveToRole, setSaveToRole] = useState(true);
  const [jdBusy, setJdBusy] = useState(false);
  const [jdNote, setJdNote] = useState<string | null>(null);
  const fileInput = useRef<HTMLInputElement>(null);

  // Start from the job description saved on the role, if it has one.
  useEffect(() => {
    if (!roleId) return;
    let live = true;
    setJdNote(null);
    hiringApi
      .roleJobDescription(roleId, org)
      .then((r) => {
        if (!live) return;
        setRoleJobDescription(r.jobDescription || '');
        setJobDescription(r.jobDescription || '');
      })
      .catch(() => live && setRoleJobDescription(''));
    return () => {
      live = false;
    };
  }, [roleId, org]);

  const uploadJobDescription = async (file: File) => {
    setJdBusy(true);
    setJdNote(null);
    try {
      const r = await hiringApi.extractJobDescription(
        { fileBase64: await toBase64(file), fileName: file.name, contentType: file.type || 'text/plain' },
        org
      );
      setJobDescription(r.text);
      setJdNote(r.truncated ? `Read from ${file.name}. It was long, so only the first part was kept.` : `Read from ${file.name}. Check it and edit anything that's out of date.`);
    } catch (err) {
      setJdNote(hiringMessage(err, "That file couldn't be read. Paste the job description instead."));
    } finally {
      setJdBusy(false);
      if (fileInput.current) fileInput.current.value = '';
    }
  };
  const jdChanged = jobDescription.trim() !== roleJobDescription.trim();

  const ready = useMemo(() => roles.filter((r) => r.ready), [roles]);
  const countries = useMemo(
    () =>
      countryCodeArray
        .filter((c) => !blockedCountries.includes(c.code))
        .map((c) => ({ id: c.code, name: c.name }))
        .sort((a, b) => a.name.localeCompare(b.name)),
    [blockedCountries]
  );
  const role = ready.find((r) => r.id === roleId);
  const emailLooksRight = /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(contactEmail.trim());
  const needsState = country === 'US';
  const blocked = restricted === true;
  const missing = !name.trim()
    ? 'Name the round'
    : !roleId
    ? 'Choose the role'
    : !country
    ? 'Say where the role is based'
    : needsState && !state
    ? 'Choose the US state'
    : restricted === null
    ? 'Answer the location question'
    : blocked
    ? blockedMessage
    : !emailLooksRight
    ? 'Add a contact email for candidates'
    : null;

  return (
    <Dialog
      open={open}
      onClose={onClose}
      maxWidth="sm"
      fullWidth
      PaperProps={{ sx: { bgcolor: SURFACE, border: `1px solid ${LINE}`, borderRadius: '8px' } }}
    >
      <Box sx={{ p: '24px', display: 'flex', flexDirection: 'column', gap: '18px' }}>
        <Box>
          <Typography sx={{ fontFamily: FONT, fontSize: 18, fontWeight: 600, color: TEXT }}>
            New Hiring Round
          </Typography>
          <Typography sx={{ ...BODY, mt: '4px' }}>
            One hiring process for one role. A role can have more than one round open at a time.
          </Typography>
        </Box>

        <Field
          label="Round name"
          required
          help="What you will recognise it by, for example the role, the city and the month."
        >
          <Box
            component="input"
            value={name}
            placeholder="Account Executive, London, Nov 2026"
            onChange={(e: React.ChangeEvent<HTMLInputElement>) => setName(e.target.value)}
            sx={INPUT_SX}
          />
        </Field>

        <Field
          label="Role"
          required
          help="Candidates are graded against this role's capability levels, so only roles whose levels are confirmed in Capabilities can be used."
        >
          <SelectField
            label="Role"
            options={ready.map((r) => ({ id: r.id, name: `${r.name} (${r.teamName})` }))}
            value={roleId}
            placeholder={ready.length ? 'Choose a role' : 'No role has its levels confirmed yet'}
            onChange={setRoleId}
          />
          {role && (
            <Typography sx={{ ...BODY, fontSize: 12, mt: '6px' }}>
              {role.capabilities.length} {role.capabilities.length === 1 ? 'capability' : 'capabilities'} will be
              assessed. You can deselect any that are not relevant to hiring when you build the assessment.
            </Typography>
          )}
        </Field>

        {role && (
          <Field
            label="Job description"
            help="Elliott writes each Roleplay from this, so the situations describe this job: its duties, who it works with, the product and the customers. Optional, but without it the situations describe a made-up company."
          >
            <Box
              component="textarea"
              value={jobDescription}
              rows={6}
              maxLength={MAX_JD}
              placeholder="Paste the job description or describe the role: what it owns, who it works with, what the company sells and to whom."
              onChange={(e: React.ChangeEvent<HTMLTextAreaElement>) => setJobDescription(e.target.value)}
              sx={{ ...TEXTAREA_SX, minHeight: 120 }}
              aria-label="Job description"
            />
            <Box sx={{ display: 'flex', alignItems: 'center', gap: '12px', mt: '8px', flexWrap: 'wrap' }}>
              <input
                ref={fileInput}
                type="file"
                accept={JD_TYPES}
                hidden
                onChange={(e) => e.target.files?.[0] && uploadJobDescription(e.target.files[0])}
              />
              <ActionButton onClick={() => fileInput.current?.click()} disabled={jdBusy}>
                {jdBusy ? 'Reading...' : 'Upload a file'}
              </ActionButton>
              {jobDescription.trim() && jdChanged && (
                <Box component="label" sx={{ ...BODY, fontSize: 13, display: 'flex', alignItems: 'center', gap: '6px', cursor: 'pointer' }}>
                  <input type="checkbox" checked={saveToRole} onChange={(e) => setSaveToRole(e.target.checked)} />
                  Save it to {role.name} for future rounds
                </Box>
              )}
            </Box>
            {jdNote && <Typography sx={{ ...BODY, fontSize: 12, mt: '6px' }}>{jdNote}</Typography>}
          </Field>
        )}

        <Field
          label="Where is the job based?"
          required
          help="This decides which notices and consents candidates see, and how long their data is kept. It can't be changed once anyone has been invited."
        >
          <SelectField
            label="Country"
            options={countries}
            value={country}
            placeholder="Choose a country"
            onChange={(id) => {
              setCountry(id);
              setState(null);
            }}
          />
          {needsState && (
            <Box sx={{ mt: '8px' }}>
              <SelectField
                label="State"
                options={US_STATES}
                value={state}
                placeholder="Choose a state"
                onChange={setState}
              />
            </Box>
          )}
        </Field>

        <Field label="Is this role based in mainland China, Hong Kong or New York City?" required>
          <Box sx={{ display: 'flex', gap: '8px' }}>
            {[
              { value: false, label: 'No' },
              { value: true, label: 'Yes' }
            ].map((option) => (
              <Box
                key={option.label}
                component="button"
                type="button"
                onClick={() => setRestricted(option.value)}
                sx={{
                  ...INPUT_SX,
                  width: 'auto',
                  px: '16px',
                  cursor: 'pointer',
                  color: restricted === option.value ? TEXT : TEXT_2,
                  borderColor: restricted === option.value ? '#F79420' : undefined
                }}
              >
                {option.label}
              </Box>
            ))}
          </Box>
          {blocked && (
            <Box sx={{ mt: '10px' }}>
              <Note>{blockedMessage}</Note>
            </Box>
          )}
        </Field>

        <Field
          label="Contact for candidates"
          required
          help="Candidates are told to use this address if they wish to ask for deletion of their data, for a human review of their assessment, or an explanation of how AI was used. Your Customer Success Manager at Dosen can assist you with replies if needed."
        >
          <Box
            component="input"
            type="email"
            value={contactEmail}
            placeholder="people@yourcompany.com"
            onChange={(e: React.ChangeEvent<HTMLInputElement>) => setContactEmail(e.target.value)}
            sx={INPUT_SX}
          />
        </Field>

        {error && (
          <Typography sx={{ ...BODY, color: DANGER }} role="alert">
            {error}
          </Typography>
        )}

        <Box sx={{ display: 'flex', justifyContent: 'flex-end', gap: '8px' }}>
          <ActionButton onClick={onClose}>Cancel</ActionButton>
          <ActionButton
            primary
            disabled={Boolean(missing) || busy || jdBusy}
            reason={missing || undefined}
            onClick={() =>
              onCreate({
                name: name.trim(),
                roleId: roleId as string,
                hiringLocation: { country: country as string, state: needsState ? (state as string) : '' },
                // The owner's actual answer, not a literal: the button is
                // disabled on a yes, and the backend refuses one too, so the
                // stored answer is the one they gave (H-4c, L-8).
                restrictedLocation: Boolean(restricted),
                contactEmail: contactEmail.trim(),
                jobDescription: jobDescription.trim(),
                saveJobDescriptionToRole: jdChanged && Boolean(jobDescription.trim()) && saveToRole
              })
            }
          >
            {busy ? 'Creating...' : 'Create Round'}
          </ActionButton>
        </Box>
      </Box>
    </Dialog>
  );
};

export default CreateRoundDialog;
